import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import styles from './LandingPage.module.css';
import ChatWidget from './ChatWidget';
import SiteNav from './SiteNav';
import SiteFooter from './SiteFooter';
import { sanitizePhoneInput, toEnglishDigits } from '../utils/phone';
import { detectLanguageFromHost, SUPPORTED_LANGUAGES } from '../i18n/config';

// هیروی سه‌بعدی نامه‌های هاگوارتز — lazy تا باندل three.js لود اولیه صفحه را
// سنگین نکند (chunk جدا می‌شود و بعد از رندر اولیه دانلود می‌شود).
const HogwartsLetterHero = lazy(() => import('./HogwartsLetterHero'));

// گوی زرین سه‌بعدی که روی کل صفحه پرواز می‌کند و از موس فرار می‌کند —
// lazy به همان دلیل بالا (باندل three.js در chunk جدا می‌ماند).
const GoldenSnitch = lazy(() => import('./GoldenSnitch'));

// ── detect language from hostname + localStorage ───────────────────────────
const hostname = window.location.hostname;
const _storedLang = (() => { try { return localStorage.getItem('language'); } catch { return null; } })();
const detectedLang = (_storedLang && SUPPORTED_LANGUAGES.includes(_storedLang))
  ? _storedLang
  : detectLanguageFromHost(hostname);
const IS_PERSIAN = detectedLang === 'fa';

const T = {
  fa: {
    dir: 'rtl',
    logo: 'جامعه پاترهدها',
    tagline: 'دنیای جادو، تجربه‌ای واقعی',
    heroImage: '/uploads/pic-1---Copy.png',
    heroTitle: 'به هاگوارتز خوش آمدید',
    heroSubtitle: 'تجربه‌ای تعاملی در دنیای هری پاتر؛ از دریافت دعوت‌نامه و گروه‌بندی تا انتخاب چوبدستی، آشکار شدن پاترونوس و ماجراجویی سال‌به‌سال در هاگوارتز.',
    heroBullets: ['دعوت‌نامه اختصاصی', 'آزمون‌های جادویی واقعی', 'تصاویر ساخته‌شده با هوش مصنوعی', 'داشبورد دانش‌آموزی'],
    ctaText: 'دریافت دعوت‌نامه',
    storyLabel: 'نقشه راه جادویی',
    storyTitle: 'بعد از ورود چه اتفاقی می‌افتد؟',
    storyDesc: 'اینجا فقط یک فرم ثبت‌نام نیست؛ شما وارد یک تجربه داستانی می‌شوید که هر تصمیم، آزمون و مأموریت روی پرونده جادویی‌تان اثر می‌گذارد.',
    journeySteps: [
      { step: '1', image: '/uploads/step-1-invitation.png', title: 'دعوت‌نامه می‌گیری', desc: 'در لیست انتظار ثبت‌نام کن؛ به‌محض باز شدن ظرفیت، جغدی دعوت‌نامه‌ات را می‌آورد.' },
      { step: '2', image: '/uploads/step-2-profile.png', title: 'پرونده جادویی می‌سازی', desc: 'نام، تاریخ تولد و عکس چهره‌ات را ثبت می‌کنی تا پروفایل و تصاویر خانه‌ات ساخته شود.' },
      { step: '3', image: '/uploads/step-3-tests.png', title: 'آزمون‌ها را می‌گذرانی', desc: 'کلاه گروه‌بندی خانه‌ات را انتخاب می‌کند، چوبدستی تو را برمی‌گزیند و پاترونوست آشکار می‌شود.' },
      { step: '4', image: '/uploads/step-4-hogwarts-years.png', title: 'سال‌های هاگوارتز را جلو می‌بری', desc: 'درخت مهارت، مأموریت‌ها، فروشگاه‌ها، گالیون‌ها و برخوردهای تصادفی قلعه تجربه را زنده‌تر می‌کنند.' }
    ],
    featuresTitle: 'ماجراجویی شما در هاگوارتز',
    magicShowcaseLabel: 'چیزی فراتر از یک سایت',
    magicShowcaseTitle: 'یک هویت جادویی کامل می‌سازی',
    magicShowcaseDesc: 'هر آزمون و انتخاب، بخشی از پرونده تو را شکل می‌دهد؛ از تصویر چوبدستی و پاترونوس تا لباس خانه و مسیر مهارت‌ها.',
    magicShowcase: [
      { icon: '🖼️', title: 'تصاویر جادویی با هوش مصنوعی', desc: 'برای چوبدستی، پاترونوس و لباس خانه‌ات تصاویر اختصاصی در پنل کاربری ساخته می‌شود.' },
      { icon: '📚', title: 'درخت مهارت هفت‌ساله', desc: 'از وینگاردیوم لویوسا تا پروتگو توتالوم، مهارت‌ها سال‌به‌سال باز می‌شوند.' },
      { icon: '🏰', title: 'قلعه زنده', desc: 'پله‌های جابه‌جاشونده، تابلوهای سخنگو، راه‌های مخفی و شیطنت‌های بدعنق (روح شرور قلعه) نمی‌گذارند هیچ مسیری تکراری شود.' }
    ],
    moreFeatures: [
      { icon: '🏠', title: 'خانه‌های هاگوارتز', desc: 'گریفیندور، هافلپاف، ریونکلاو یا اسلیترین' },
      { icon: '🛒', title: 'خرید از کوچه دیاگون', desc: 'وسایل مدرسه‌ات را از فروشگاه‌ها بخر' },
      { icon: '🚂', title: 'هاگوارتز اکسپرس', desc: 'از سکوی ۹¾ سوار قطار شو' },
      { icon: '💰', title: '۵۰ گالیون هدیه', desc: 'با هر دعوت‌نامه ۵۰ گالیون بگیر' }
    ],
    waitlistTitle: 'به لیست انتظار بپیوندید',
    waitlistDesc: 'دعوت‌نامه‌ها فقط برای جادوگران برگزیده فرستاده می‌شود. شماره موبایل خود را وارد کنید تا به‌محض ارسال دعوت‌نامه‌ها باخبر شوید.',
    waitlistIcon: '📬',
    countdownLabel: 'مهلت ثبت‌نام در لیست انتظار',
    countdownMonths: 'ماه',
    countdownDays: 'روز',
    countdownHours: 'ساعت',
    countdownMinutes: 'دقیقه',
    countdownSeconds: 'ثانیه',
    countdownExpired: 'مهلت ثبت‌نام در لیست انتظار به پایان رسیده است.',
    countdownTotalSeconds: 'ثانیه تا پایان مهلت ثبت‌نام',
    waitlistStatsWaiting: 'نفر در لیست انتظار هستند',
    waitlistStatsCapacity: 'ظرفیت کل ثبت‌نام',
    waitlistStatsRemaining: 'جای خالی باقی‌مانده',
    waitlistFullMsg: 'ظرفیت لیست انتظار تکمیل شده است.',
    phonePlaceholder: '09123456789',
    codePlaceholder: 'کد ۶ رقمی',
    sendCode: 'ارسال کد',
    verify: 'تأیید و ثبت‌نام',
    successMsg: '✓ در لیست انتظار ثبت شدی! به‌محض ارسال دعوت‌نامه‌ها با پیامک خبرت می‌کنیم.',
    features: [
      { icon: '🎩', title: 'کلاه گروه‌بندی', desc: 'آزمون شخصیت‌شناسی برای تعیین خانه‌ات در هاگوارتز' },
      { icon: '🪄', title: 'چوبدستی', desc: 'این چوبدستی است که جادوگرش را انتخاب می‌کند؛ ببین کدام‌یک تو را برمی‌گزیند' },
      { icon: '✨', title: 'پاترونوس', desc: 'کشف پاترونوس منحصربه‌فرد خودت' },
    ],
    supportTitle: 'حمایت مالی از انجمن',
    supportDesc: 'اگر دوست داری به ادامه ساخت این تجربه جادویی کمک کنی، می‌توانی از طریق ریمیت حامی ما شوی. اگر اجازه بدهی، نام و مبلغ حمایتت همین‌جا نمایش داده می‌شود.',
    supportButton: 'حمایت در ریمیت',
    supportersTitle: 'تازه‌ترین حامیان',
    anonymousSupporter: 'حامی ناشناس',
    noSupporters: 'هنوز حمایتی ثبت نشده؛ اولین حامی این ماجراجویی باش!',
    contactTitle: 'ارتباط با ما',
    contactDesc: 'اگر سوال، پیشنهاد یا مشکلی دارید، فرم زیر را پر کنید تا مستقیماً به وزارت سحر و جادو و مدیران جامعه ارسال شود. همچنین می‌توانید از طریق دکمه‌های زیر به کانال تلگرام یا انجمن پاترهدها بپیوندید.',
    contactFormName: 'نام شما',
    contactFormEmail: 'ایمیل یا شماره تماس',
    contactFormSubject: 'موضوع پیام (اختیاری)',
    contactFormMessage: 'متن پیام شما...',
    contactSubmit: '🪄 ارسال پیام به هاگوارتز',
    contactSuccess: '✓ پیام شما با موفقیت به جغدها سپرده شد!',
    contactLinks: [
      { icon: '✈️', image: '', imageWidth: '', imageHeight: '', label: 'کانال تلگرام ما', url: 'https://t.me/WwWPotterHeadIR' },
      { icon: '👥', image: '', imageWidth: '', imageHeight: '', label: 'انجمن گفتگو', url: '/forum' }
    ],
    langSwitch: 'English',
    langSwitchUrl: 'https://potterheads.org',
    footer: '© ۱۴۰۵ جامعه پاترهدها — تمامی حقوق محفوظ است',
    sending: 'در حال ارسال...',
    phoneError: 'شماره موبایل معتبر نیست (مثلاً ۰۹۱۲۳۴۵۶۷۸۹)',
    alreadyRegistered: 'این شماره قبلاً ثبت شده است.',
  },
  en: {
    dir: 'ltr',
    logo: 'Potterheads Community',
    tagline: 'The Magic World, A Real Experience',
    heroImage: '/uploads/pic-1---Copy.png',
    heroTitle: 'Welcome to Hogwarts',
    heroSubtitle: 'An interactive Harry Potter-inspired experience: invitations, Sorting Hat, wand discovery, Patronus reveal, AI-generated magical images, and a year-by-year Hogwarts journey.',
    heroBullets: ['Exclusive invitation', 'Magical personality tests', 'AI-generated visuals', 'Student dashboard'],
    ctaText: 'Get Your Invitation',
    storyLabel: 'Magical Roadmap',
    storyTitle: 'What happens after you join?',
    storyDesc: 'This is more than a signup form. You step into a story-driven magical profile where every test, mission, and choice shapes your Hogwarts identity.',
    journeySteps: [
      { step: '1', image: '/uploads/step-1-invitation.png', title: 'Receive your invitation', desc: 'Join the waitlist and get notified when your magical access opens.' },
      { step: '2', image: '/uploads/step-2-profile.png', title: 'Create your student profile', desc: 'Set up your name, birthday, and face photo so your house portraits can be generated.' },
      { step: '3', image: '/uploads/step-3-tests.png', title: 'Take the magical tests', desc: 'The Sorting Hat chooses your house, your wand is revealed, and your Patronus takes form.' },
      { step: '4', image: '/uploads/step-4-hogwarts-years.png', title: 'Progress through Hogwarts years', desc: 'Unlock skills, missions, shops, galleons, and random castle encounters as your story grows.' }
    ],
    featuresTitle: 'Your Hogwarts Adventure',
    magicShowcaseLabel: 'More than a website',
    magicShowcaseTitle: 'Build a complete magical identity',
    magicShowcaseDesc: 'Every test and choice shapes your profile: wand visuals, Patronus artwork, house outfit portraits, and a living skill path.',
    magicShowcase: [
      { icon: '🖼️', title: 'AI Magical Images', desc: 'Your wand, Patronus, and house outfit portraits are generated and saved to your dashboard.' },
      { icon: '📚', title: 'Seven-Year Skill Tree', desc: 'From Wingardium Leviosa to Protego Totalum, spells unlock across the Hogwarts years.' },
      { icon: '🏰', title: 'Living Castle', desc: 'Moving stairs, portraits, secret passages, and Peeves-style chaos keep exploration unpredictable.' }
    ],
    moreFeatures: [
      { icon: '🏠', title: 'Hogwarts Houses', desc: 'Gryffindor, Hufflepuff, Ravenclaw, or Slytherin' },
      { icon: '🛒', title: 'Diagon Alley Shopping', desc: 'Buy your school supplies from wizarding shops' },
      { icon: '🚂', title: 'Hogwarts Express', desc: 'Board the train from Platform 9¾' },
      { icon: '💰', title: '50 Galleons Gift', desc: 'Receive 50 Galleons with your invitation' }
    ],
    waitlistTitle: 'Join the Waitlist',
    waitlistDesc: 'Invitations are sent exclusively to selected witches and wizards. Enter your name and email to be notified when your invitation arrives.',
    waitlistIcon: '📬',
    countdownLabel: 'Time left to join the waitlist',
    countdownMonths: 'Months',
    countdownDays: 'Days',
    countdownHours: 'Hours',
    countdownMinutes: 'Minutes',
    countdownSeconds: 'Seconds',
    countdownExpired: 'Waitlist registration has closed.',
    countdownTotalSeconds: 'seconds left to register',
    waitlistStatsWaiting: 'people are on the waitlist',
    waitlistStatsCapacity: 'Total capacity',
    waitlistStatsRemaining: 'spots remaining',
    waitlistFullMsg: 'The waitlist is full.',
    firstNamePlaceholder: 'First name',
    lastNamePlaceholder:  'Last name',
    emailPlaceholder: 'your@email.com',
    joinWaitlist: 'Join Waitlist',
    successMsg: "✓ You're on the waitlist! Check your inbox for a confirmation email.",
    features: [
      { icon: '🎩', title: 'Sorting Hat', desc: 'Take the personality quiz to be sorted into your Hogwarts house' },
      { icon: '🪄', title: 'Wand Selection', desc: 'The wand chooses the wizard — discover yours' },
      { icon: '✨', title: 'Patronus', desc: 'Reveal your unique Patronus charm' },
    ],
    supportTitle: 'Support the Community',
    supportDesc: 'Help us keep building this magical experience. Supporters can be shown here with their name and amount only if they choose to share them.',
    supportButton: 'Support with Crypto',
    supportersTitle: 'Recent Supporters',
    anonymousSupporter: 'Anonymous supporter',
    noSupporters: 'No support has been recorded yet. Be the first supporter of this adventure!',
    contactTitle: 'Contact Us',
    contactDesc: 'If you have any questions, feedback, or issues, please fill out the form below to send a message straight to our Hogwarts governors. You can also join our global Telegram community or discussion forum below.',
    contactFormName: 'Your Name',
    contactFormEmail: 'Email or Phone Number',
    contactFormSubject: 'Subject (Optional)',
    contactFormMessage: 'Your message...',
    contactSubmit: '🪄 Send Owl Message',
    contactSuccess: '✓ Your message has been sent to the owls!',
    contactLinks: [
      { icon: '✈️', image: '', imageWidth: '', imageHeight: '', label: 'Telegram Channel', url: 'https://t.me/potterheads_org' },
      { icon: '👥', image: '', imageWidth: '', imageHeight: '', label: 'Discussion Forum', url: '/forum' }
    ],
    langSwitch: 'فارسی',
    langSwitchUrl: 'https://potterhead.ir',
    footer: '© 2025 Potterheads Community — All Rights Reserved',
    sending: 'Sending...',
    firstNameError: 'First name must be at least 2 characters.',
    lastNameError: 'Last name must be at least 2 characters.',
    emailError: 'Please enter a valid email address.',
    alreadyRegistered: 'This email is already on the waitlist.',
  }
};

const lang = detectedLang; // 'fa', 'en', 'ja', 'de', etc.
const t = T[IS_PERSIAN ? 'fa' : 'en']; // hardcoded defaults only have fa/en; others share en layout
const API_BASE = '/api/waitlist';

// ── Stars background ───────────────────────────────────────────────────────
function StarField() {
  const stars = Array.from({ length: 120 }, (_, i) => ({
    id: i,
    top: Math.random() * 100,
    left: Math.random() * 100,
    size: Math.random() * 2.5 + 0.5,
    delay: Math.random() * 4,
    duration: Math.random() * 3 + 2,
  }));
  return (
    <div className={styles.starField} aria-hidden>
      {stars.map(s => (
        <span
          key={s.id}
          className={styles.star}
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

const JALALI_MONTHS = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
const CURRENT_JALALI_YEAR = Number(
  new Intl.DateTimeFormat('en-US-u-ca-persian', { year: 'numeric' })
    .format(new Date())
    .replace(/[^\d]/g, '')
);
const JALALI_YEARS = Array.from({ length: CURRENT_JALALI_YEAR - 1300 + 1 }, (_, i) => CURRENT_JALALI_YEAR - i);

function jalaliDaysInMonth(jm) {
  if (!jm) return 31;
  const m = Number(jm);
  if (m >= 1 && m <= 6) return 31;
  if (m >= 7 && m <= 11) return 30;
  return 30; // اسفند
}

// ── Persian waitlist form (name + phone + OTP) ───────────────────────────
function PersianWaitlistForm() {
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [phone,     setPhone]     = useState('');
  const [otp,       setOtp]       = useState('');
  const [jy,        setJy]        = useState('');
  const [jm,        setJm]        = useState('');
  const [jd,        setJd]        = useState('');
  const [step,      setStep]      = useState('info'); // info | otp | done
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [cooldown,  setCooldown]  = useState(0);
  const [doneMsg,   setDoneMsg]   = useState('');
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startCooldown = () => {
    setCooldown(120);
    timerRef.current = setInterval(() => {
      setCooldown(c => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const handleSendOTP = async () => {
    setError('');
    if (!firstName.trim() || firstName.trim().length < 2) {
      setError('نام باید حداقل ۲ حرف باشد.');
      return;
    }
    if (!lastName.trim() || lastName.trim().length < 2) {
      setError('نام خانوادگی باید حداقل ۲ حرف باشد.');
      return;
    }
    if (!/^09[0-9]{9}$/.test(phone.trim())) {
      setError(t.phoneError);
      return;
    }
    setLoading(true);
    try {
      const birthDateValue = (jy && jm && jd) ? { jy: Number(jy), jm: Number(jm), jd: Number(jd) } : null;
      await axios.post(`${API_BASE}/request-otp`, {
        phone:     phone.trim(),
        firstName: firstName.trim(),
        lastName:  lastName.trim(),
        birthDate: birthDateValue
      });
      setStep('otp');
      startCooldown();
    } catch (e) {
      const msg = e.response?.data?.message;
      if (e.response?.status === 409) {
        setDoneMsg(t.alreadyRegistered);
        setStep('done');
      } else {
        setError(msg || 'خطا در ارسال کد');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    setError('');
    if (!otp || otp.length !== 6) {
      setError('کد ۶ رقمی وارد کنید');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/verify-otp`, {
        phone: phone.trim(),
        otp:   otp.trim()
      });
      setDoneMsg(res.data?.message || t.successMsg);
      setStep('done');
    } catch (e) {
      setError(e.response?.data?.message || 'کد واردشده درست نیست.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    setOtp('');
    setError('');
    setStep('info');
  };

  if (step === 'done') {
    return <p className={styles.successMsg}>{doneMsg || t.successMsg}</p>;
  }

  return (
    <div className={styles.waitlistForm}>
      {step === 'info' && (
        <>
          <div className={styles.nameRow}>
            <input
              className={styles.input}
              type="text"
              placeholder="نام"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              maxLength={40}
            />
            <input
              className={styles.input}
              type="text"
              placeholder="نام خانوادگی"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              maxLength={60}
            />
          </div>
          
          <div style={{ marginTop: '10px', marginBottom: '10px', display: 'flex', gap: '8px', width: '100%', alignItems: 'center' }}>
            <span style={{ color: '#aaa', fontSize: '13px', minWidth: '80px', textAlign: 'right' }}>تاریخ تولد:</span>
            <select
              value={jd}
              onChange={(e) => setJd(e.target.value)}
              style={{ flex: 1, padding: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#fff' }}
            >
              <option value="" style={{ background: '#1b1430' }}>روز</option>
              {Array.from({ length: jalaliDaysInMonth(jm) }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d} style={{ background: '#1b1430' }}>{d}</option>
              ))}
            </select>
            <select
              value={jm}
              onChange={(e) => {
                const newJm = e.target.value;
                setJm(newJm);
                // فقط اگر روز انتخاب‌شده در ماه جدید معتبر نباشد پاک می‌شود، نه همیشه —
                // قبلاً هر تغییر ماه، روزِ از قبل انتخاب‌شده را پاک می‌کرد و کاربر مجبور
                // بود دوباره روز را انتخاب کند.
                if (jd && Number(jd) > jalaliDaysInMonth(newJm)) setJd('');
              }}
              style={{ flex: 1, padding: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#fff' }}
            >
              <option value="" style={{ background: '#1b1430' }}>ماه</option>
              {JALALI_MONTHS.map((name, i) => (
                <option key={name} value={i + 1} style={{ background: '#1b1430' }}>{name}</option>
              ))}
            </select>
            <select
              value={jy}
              onChange={(e) => setJy(e.target.value)}
              style={{ flex: 1, padding: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#fff' }}
            >
              <option value="" style={{ background: '#1b1430' }}>سال</option>
              {JALALI_YEARS.map((y) => (
                <option key={y} value={y} style={{ background: '#1b1430' }}>{y}</option>
              ))}
            </select>
          </div>

          <div className={styles.inputRow}>
            <input
              className={styles.input}
              type="tel"
              dir="ltr"
              placeholder={t.phonePlaceholder}
              value={phone}
              onChange={e => setPhone(sanitizePhoneInput(e.target.value))}
              onKeyDown={e => e.key === 'Enter' && handleSendOTP()}
              maxLength={11}
            />
            <button className={styles.btn} onClick={handleSendOTP} disabled={loading}>
              {loading ? t.sending : t.sendCode}
            </button>
          </div>
        </>
      )}
      {step === 'otp' && (
        <>
          <p className={styles.otpHint}>
            کد تأیید به <strong>{phone}</strong> پیامک شد (اعتبار: ۲ دقیقه):
          </p>
          <div className={styles.inputRow}>
            <input
              className={styles.input}
              type="text"
              dir="ltr"
              inputMode="numeric"
              placeholder={t.codePlaceholder}
              value={otp}
              onChange={e => setOtp(toEnglishDigits(e.target.value).replace(/\D/g, ''))}
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
              maxLength={6}
              autoFocus
            />
            <button className={styles.btn} onClick={handleVerify} disabled={loading}>
              {loading ? t.sending : t.verify}
            </button>
          </div>
          <button
            className={styles.resendBtn}
            onClick={handleResend}
            disabled={cooldown > 0}
          >
            {cooldown > 0 ? `ارسال مجدد (${cooldown} ثانیه)` : 'ارسال مجدد کد'}
          </button>
        </>
      )}
      {error && <p className={styles.errorMsg}>{error}</p>}
    </div>
  );
}

// ── English waitlist form (name + email) ──────────────────────────────────
function EnglishWaitlistForm() {
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [email,     setEmail]     = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [loading,   setLoading]   = useState(false);
  const [done,      setDone]      = useState(false);
  const [error,     setError]     = useState('');

  const handleSubmit = async () => {
    setError('');
    if (!firstName.trim() || firstName.trim().length < 2) {
      setError(t.firstNameError);
      return;
    }
    if (!lastName.trim() || lastName.trim().length < 2) {
      setError(t.lastNameError);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(t.emailError);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/email`, {
        firstName: firstName.trim(),
        lastName:  lastName.trim(),
        email:     email.trim(),
        birthDate: birthDate ? birthDate : null
      });
      setDone(true);
    } catch (e) {
      const msg = e.response?.data?.message;
      if (e.response?.status === 409) setDone(true);
      else setError(msg || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (done) return <p className={styles.successMsg}>{t.successMsg}</p>;

  return (
    <div className={styles.waitlistForm}>
      <div className={styles.nameRow}>
        <input
          className={styles.input}
          type="text"
          placeholder={t.firstNamePlaceholder}
          value={firstName}
          onChange={e => setFirstName(e.target.value)}
          maxLength={40}
        />
        <input
          className={styles.input}
          type="text"
          placeholder={t.lastNamePlaceholder}
          value={lastName}
          onChange={e => setLastName(e.target.value)}
          maxLength={60}
        />
      </div>
      
      <div style={{ marginTop: '10px', marginBottom: '10px', display: 'flex', gap: '8px', width: '100%', alignItems: 'center' }}>
        <span style={{ color: '#aaa', fontSize: '13px', minWidth: '80px', textAlign: 'left' }}>Birthdate:</span>
        <input
          type="date"
          className={styles.input}
          value={birthDate}
          onChange={e => setBirthDate(e.target.value)}
          style={{ flex: 1, padding: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', color: '#fff' }}
        />
      </div>

      <div className={styles.inputRow}>
        <input
          className={styles.input}
          type="email"
          placeholder={t.emailPlaceholder}
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
        />
        <button className={styles.btn} onClick={handleSubmit} disabled={loading}>
          {loading ? t.sending : t.joinWaitlist}
        </button>
      </div>
      {error && <p className={styles.errorMsg}>{error}</p>}
    </div>
  );
}

// ── تیزر کوچک حمایت مالی روی صفحه اصلی — با کلیک به صفحه‌ی جداگانه‌ی
// /support می‌رود که محتوای کامل (توضیح + دکمه + لیست حامیان) آنجاست.
// قبلاً کل بخش حمایت مالی همین‌جا (روی صفحه اصلی) رندر می‌شد.
function SupportTeaser({ content }) {
  return (
    <section className={styles.supportSection} id="support">
      <div className={styles.container}>
        <div className={styles.supportCard}>
          <div className={styles.supportIntro}>
            <span>💛</span>
            <h2>{content.supportTitle}</h2>
            <p>{content.supportDesc}</p>
            <Link className={styles.supportBtn} to="/support">
              {content.supportButton}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Waitlist deadline countdown (days/hours/minutes/seconds) ──────────────
function getCountdownParts(targetDate) {
  const diff = new Date(targetDate).getTime() - Date.now();
  if (Number.isNaN(diff) || diff <= 0) return null;
  const totalDays = Math.floor(diff / 86400000);
  return {
    // ماه به‌صورت ۳۰روزه تقریب زده می‌شود و باقی‌مانده‌اش «روز» است
    months: Math.floor(totalDays / 30),
    days: totalDays % 30,
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
    // مجموع ثانیه‌های باقی‌مانده — برای شمارنده‌ی ثانیه‌ای (قابل کنترل از پنل ادمین)
    totalSeconds: Math.floor(diff / 1000),
  };
}

// اعداد در نسخه فارسی با ارقام فارسی نمایش داده می‌شوند
const fmtNum = (n) => Number(n).toLocaleString(IS_PERSIAN ? 'fa-IR' : 'en-US');

function WaitlistCountdown({ targetDate, content, align = 'center', showTotalSeconds = false }) {
  const [parts, setParts] = useState(() => getCountdownParts(targetDate));

  useEffect(() => {
    setParts(getCountdownParts(targetDate));
    if (!targetDate) return undefined;
    const id = setInterval(() => setParts(getCountdownParts(targetDate)), 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  if (!targetDate) return null;

  if (!parts) {
    return (
      <div className={styles.countdown} style={{ textAlign: align }}>
        <p className={styles.countdownExpired}>{content.countdownExpired}</p>
      </div>
    );
  }

  const units = [
    { value: parts.months, label: content.countdownMonths || t.countdownMonths },
    { value: parts.days, label: content.countdownDays },
    { value: parts.hours, label: content.countdownHours },
    { value: parts.minutes, label: content.countdownMinutes },
    { value: parts.seconds, label: content.countdownSeconds },
  ];

  return (
    <div className={styles.countdown} style={{ textAlign: align }}>
      <p className={styles.countdownLabel}>{content.countdownLabel}</p>
      <div className={styles.countdownGrid} style={{ justifyContent: align === 'right' ? 'flex-end' : align === 'left' ? 'flex-start' : 'center' }}>
        {units.map((u) => (
          <div className={styles.countdownUnit} key={u.label}>
            <span className={styles.countdownValue}>{String(u.value).padStart(2, '0')}</span>
            <span className={styles.countdownUnitLabel}>{u.label}</span>
          </div>
        ))}
      </div>
      {showTotalSeconds && (
        <p style={{ marginTop: 10, fontSize: '0.95rem', opacity: 0.85, direction: t.dir }}>
          <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(parts.totalSeconds)}</span>
          {' '}{content.countdownTotalSeconds || t.countdownTotalSeconds}
        </p>
      )}
    </div>
  );
}

// ── آمار لیست انتظار (تعداد در انتظار / ظرفیت / جای خالی) ──────────────────
// همه از /api/waitlist/stats می‌آید که خودش تابع toggleها و ظرفیت تنظیم‌شده
// در پنل ادمین است — اگر نمایش هر بخش خاموش باشد، همان بخش رندر نمی‌شود.
function WaitlistStats({ stats, content }) {
  if (!stats) return null;
  const chips = [];
  if (stats.showCount && stats.count !== null && stats.count !== undefined) {
    chips.push({ key: 'count', value: fmtNum(stats.count), label: content.waitlistStatsWaiting || t.waitlistStatsWaiting });
  }
  if (stats.showCapacity && stats.capacity) {
    chips.push({ key: 'capacity', value: fmtNum(stats.capacity), label: content.waitlistStatsCapacity || t.waitlistStatsCapacity });
    if (stats.remaining !== null && stats.remaining !== undefined) {
      chips.push({ key: 'remaining', value: fmtNum(stats.remaining), label: content.waitlistStatsRemaining || t.waitlistStatsRemaining });
    }
  }
  if (chips.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', margin: '14px 0 6px', direction: t.dir }}>
      {chips.map((c) => (
        <span
          key={c.key}
          style={{
            padding: '8px 14px',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.15)',
            fontSize: '0.9rem',
          }}
        >
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{c.value}</strong> {c.label}
        </span>
      ))}
    </div>
  );
}

// ── تشخیص حالت موبایل (برای انتخاب سایز جداگانه‌ی تصاویر روی موبایل) ────────
function useIsMobile(breakpoint = 700) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, [breakpoint]);
  return isMobile;
}

// ── استایل عرض/ارتفاع یک تصویر با در نظر گرفتن سایز اختصاصی موبایل ─────────
// هر آیتم می‌تواند imageWidth/imageHeight (دسکتاپ) و اختیاراً
// imageWidthMobile/imageHeightMobile داشته باشد؛ در موبایل اگر سایز مخصوص
// موبایل ست نشده باشد، همان سایز دسکتاپ استفاده می‌شود (و اگر هیچ‌کدام ست
// نشده باشد، رفتار ریسپانسیو پیش‌فرض CSS دست‌نخورده می‌ماند).
function imageSizeStyle(item, isMobile, { noMaxWidth = false } = {}) {
  if (!item) return {};
  const w = (isMobile && item.imageWidthMobile) || item.imageWidth;
  const h = (isMobile && item.imageHeightMobile) || item.imageHeight;
  return {
    ...(w ? { width: `${w}px`, ...(noMaxWidth ? { maxWidth: 'none' } : {}) } : {}),
    ...(h ? { height: `${h}px` } : {}),
  };
}

// ── متغیرهای CSS برای اندازه‌ی متن قابل‌تنظیم از پنل مدیریت ─────────────────
// هر «نقش» متنی (مثلاً heroTitle) به دو متغیر CSS با نام ثابت نگاشت می‌شود که
// در LandingPage.module.css به‌عنوان fallback اندازه‌ی پیش‌فرض/ریسپانسیو
// استفاده می‌شوند: --ts-<role>-d (دسکتاپ) و --ts-<role>-m (موبایل).
function textSizeVars(sizes, role) {
  const s = sizes?.[role];
  if (!s) return undefined;
  const vars = {};
  if (s.desktop) vars[`--ts-${role}-d`] = `${s.desktop}px`;
  if (s.mobile) vars[`--ts-${role}-m`] = `${s.mobile}px`;
  return Object.keys(vars).length ? vars : undefined;
}

// ── مثل imageSizeStyle، اما وقتی هیچ سایزی ست نشده یک اندازه‌ی پیش‌فرض (fallback)
// برمی‌گرداند — برای آیکون‌هایی که همیشه باید یک سایز داشته باشند (اموجی/تصویر).
function imgDims(item, isMobile, fallback) {
  const w = (isMobile && item?.imageWidthMobile) || item?.imageWidth;
  const h = (isMobile && item?.imageHeightMobile) || item?.imageHeight;
  return {
    width: w ? `${w}px` : fallback,
    height: h ? `${h}px` : fallback,
  };
}

// ── Floating particles ─────────────────────────────────────────────────────
function Particles() {
  const items = ['⚡', '🌟', '✨', '🪄', '⭐', '💫', '🔮', '🦉'];
  return (
    <div className={styles.particles} aria-hidden>
      {Array.from({ length: 14 }, (_, i) => (
        <span
          key={i}
          className={styles.particle}
          style={{
            left: `${(i * 7.3) % 100}%`,
            animationDelay: `${(i * 0.7) % 6}s`,
            animationDuration: `${6 + (i % 4)}s`,
            fontSize: `${1 + (i % 3) * 0.4}rem`,
          }}
        >
          {items[i % items.length]}
        </span>
      ))}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [navScrolled, setNavScrolled] = useState(false);
  // محتوای قابل ویرایش از پنل ادمین (هیرو، مسیر ورود، ویژگی‌ها و...) — تا وقتی
  // از سرور نرسیده از مقادیر پیش‌فرض T[lang] استفاده می‌شود تا صفحه خالی نماند.
  const [content, setContent] = useState(t);
  // اندازه‌ی متن‌ها (--ts-<role>-d/-m) — کلید مشترک بین دو زبان، جدا از fa/en
  const [textSizes, setTextSizes] = useState({});
  // آمار زنده‌ی لیست انتظار (تعداد/ظرفیت/toggleهای نمایش) — هر دقیقه تازه می‌شود
  const [wlStats, setWlStats] = useState(null);
  const isMobile = useIsMobile();
  const ts = (role) => textSizeVars(textSizes, role);
  const imgS = (item, opts) => imageSizeStyle(item, isMobile, opts);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let mounted = true;
    axios.get(`/api/landing?_=${Date.now()}`)
      .then((res) => {
        const fetched = res.data?.content?.[lang] || res.data?.content?.en;
        if (mounted && fetched) {
          setContent((prev) => ({ ...prev, ...fetched }));
        }
        if (mounted && res.data?.content?.textSizes) {
          setTextSizes(res.data.content.textSizes);
        }
      })
      .catch(() => {}); // در صورت خطا، مقادیر پیش‌فرض هاردکد باقی می‌مانند
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadStats = () => {
      axios.get(`/api/waitlist/stats?_=${Date.now()}`)
        .then((res) => {
          if (mounted && res.data?.success) setWlStats(res.data);
        })
        .catch(() => {}); // بدون آمار هم صفحه سالم می‌ماند
    };
    loadStats();
    const id = setInterval(loadStats, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  return (
    <div className={styles.page} dir={t.dir}>
      <StarField />
      <Particles />

      {/* ── گوی زرین: روی کل صفحه پرواز می‌کند و از موس می‌گریزد ── */}
      <Suspense fallback={null}>
        <GoldenSnitch />
      </Suspense>

      {/* ── NAV ── */}
      <SiteNav fixed scrolled={navScrolled} />

      {/* ── هیروی سه‌بعدی: نامه‌های پرنده هاگوارتز ── */}
      <Suspense fallback={<div style={{ height: '100vh', minHeight: 560 }} aria-hidden />}>
        <HogwartsLetterHero isPersian={IS_PERSIAN} />
      </Suspense>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.badge}>🪄 {content.tagline}</div>
          <h1 className={styles.heroTitle} style={ts('heroTitle')}>{content.heroTitle}</h1>
          <p className={styles.heroSubtitle} style={ts('heroSubtitle')}>{content.heroSubtitle}</p>
          <div className={styles.heroPills}>
            {content.heroBullets.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
          {content.waitlistCloseAt && (
            <div style={{ margin: '0 0 28px' }}>
              <WaitlistCountdown
                targetDate={content.waitlistCloseAt}
                content={content}
                align={IS_PERSIAN ? 'right' : 'left'}
                showTotalSeconds={wlStats?.showCountdownSeconds !== false}
              />
            </div>
          )}
          <a href="#waitlist" className={styles.ctaBtn}>
            {content.ctaText}
          </a>
        </div>
        <div className={styles.heroCastle} aria-hidden>
          <img
            src={content.heroImage}
            alt="Hogwarts Castle"
            className={styles.castle}
            style={imgS({
              imageWidth: content.heroImageWidth,
              imageHeight: content.heroImageHeight,
              imageWidthMobile: content.heroImageWidthMobile,
              imageHeightMobile: content.heroImageHeightMobile,
            }, { noMaxWidth: true })}
          />
          <div className={styles.castleGlow} />
        </div>
      </section>

      {/* ── JOURNEY ── */}
      <section className={styles.storySection}>
        <div className={styles.container}>
          <div className={styles.storyHeader}>
            <span>{content.storyLabel}</span>
            <h2 style={ts('storyTitle')}>{content.storyTitle}</h2>
            <p style={ts('storyDesc')}>{content.storyDesc}</p>
          </div>
          <div className={styles.journeyGrid}>
            {content.journeySteps.map((item) => (
              <div className={styles.journeyCard} key={item.step}>
                <div className={styles.journeyStep}>
                  {item.image && (
                    <img
                      // کلید = آدرس تصویر: در رندر اول محتوای پیش‌فرض هاردکد (t) نمایش
                      // داده می‌شود که مسیرهای placeholder دارد و معمولاً 404 می‌دهد؛
                      // چند لحظه بعد با پاسخ «/api/landing» جایگزین می‌شود. بدون این
                      // key، وقتی src عوض شود React همان <img> قبلی را نگه می‌دارد و
                      // style.display که onError زیر مستقیماً روی DOM ست کرده بود (برای
                      // همان 404 اولیه) هرگز پاک نمی‌شود — یعنی حتی بعد از لود موفق
                      // تصویر واقعی، همچنان مخفی می‌ماند. با key=src هر بار src عوض
                      // شود یک <img> کاملاً تازه (بدون inline style قبلی) ساخته می‌شود.
                      key={item.image}
                      src={item.image}
                      alt={item.title}
                      style={imgS(item)}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <span>{item.step}</span>
                </div>
                <h3 style={ts('journeyCardTitle')}>{item.title}</h3>
                <p style={ts('journeyCardDesc')}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className={styles.features}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle} style={ts('featuresTitle')}>
            {content.featuresTitle}
          </h2>
          <div className={styles.featureGrid}>
            {content.features.map((f, i) => (
              <div key={i} className={styles.featureCard}>
                {f.image ? (
                  <img
                    className={styles.featureIcon}
                    src={f.image}
                    alt=""
                    style={{
                      display: 'inline-block',
                      ...imgDims(f, isMobile, '3rem'),
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <div className={styles.featureIcon}>{f.icon}</div>
                )}
                <h3 className={styles.featureTitle} style={ts('featureCardTitle')}>{f.title}</h3>
                <p className={styles.featureDesc} style={ts('featureCardDesc')}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MAGIC SHOWCASE ── */}
      <section className={styles.showcaseSection}>
        <div className={styles.container}>
          <div className={styles.showcaseGrid}>
            <div className={styles.showcaseIntro}>
              <h2 style={ts('magicShowcaseTitle')}>{content.magicShowcaseTitle}</h2>
              <p style={ts('magicShowcaseDesc')}>{content.magicShowcaseDesc}</p>
            </div>
            {content.magicShowcase.map((item) => (
              <div className={styles.showcaseCard} key={item.title}>
                {item.image ? (
                  <img
                    src={item.image}
                    alt=""
                    style={{
                      display: 'block',
                      ...imgDims(item, isMobile, '4.5rem'),
                      objectFit: 'contain',
                      marginBottom: 16,
                      filter: 'drop-shadow(0 0 18px rgba(212,160,23,0.35))',
                    }}
                  />
                ) : (
                  <span>{item.icon}</span>
                )}
                <h3 style={ts('showcaseCardTitle')}>{item.title}</h3>
                <p style={ts('showcaseCardDesc')}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MORE FEATURES ── */}
      <section className={styles.moreFeatures}>
        <div className={styles.container}>
          <div className={styles.moreGrid}>
            {content.moreFeatures.map((item, i) => (
              <div key={i} className={styles.moreCard}>
                {item.image ? (
                  <img
                    className={styles.moreIcon}
                    src={item.image}
                    alt=""
                    style={{
                      ...imgDims(item, isMobile, '1.8rem'),
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <span className={styles.moreIcon}>{item.icon}</span>
                )}
                <div>
                  <strong style={ts('moreCardTitle')}>{item.title}</strong>
                  <p style={ts('moreCardDesc')}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SUPPORT (تیزر — محتوای کامل در صفحه جدا /support) ── */}
      {content.showFinancialSupport && <SupportTeaser content={content} />}

      {/* ── WAITLIST ── */}
      <section className={styles.waitlistSection} id="waitlist">
        <div className={styles.container}>
          <div className={styles.waitlistCard}>
            {content.waitlistIconImage ? (
              <img
                src={content.waitlistIconImage}
                alt=""
                style={{
                  display: 'block',
                  margin: '0 auto',
                  ...imgDims({
                    imageWidth: content.waitlistIconImageWidth,
                    imageHeight: content.waitlistIconImageHeight,
                  }, isMobile, '3.5rem'),
                  objectFit: 'contain',
                }}
              />
            ) : (
              <div className={styles.envelope}>{content.waitlistIcon || '📬'}</div>
            )}
            <h2 className={styles.waitlistTitle} style={ts('waitlistTitle')}>{content.waitlistTitle}</h2>
            <p className={styles.waitlistDesc} style={ts('waitlistDesc')}>{content.waitlistDesc}</p>
            <WaitlistStats stats={wlStats} content={content} />
            <WaitlistCountdown
              targetDate={content.waitlistCloseAt}
              content={content}
              showTotalSeconds={wlStats?.showCountdownSeconds !== false}
            />
            {/* اگر ظرفیت (تنظیم‌شده در پنل ادمین) پر شده باشد، به‌جای فرم پیام تکمیل ظرفیت نشان داده می‌شود */}
            {wlStats?.isFull ? (
              <p className={styles.errorMsg} style={{ marginTop: 12 }}>
                {content.waitlistFullMsg || t.waitlistFullMsg}
              </p>
            ) : (
              IS_PERSIAN ? <PersianWaitlistForm /> : <EnglishWaitlistForm />
            )}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <SiteFooter />

      {/* ── AI Chat (Dumbledore) ── */}
      <ChatWidget />
    </div>
  );
}
